import { AvatarDetailsButton } from "@/components/ui/avatar-details-button";
import { useImageUrl } from "@/hooks/useImage";
import { isDirectImageUrl, isImageId } from "@/utils/imageUtils";

/**
 * Default field names this component reads off an entity when no explicit
 * `*Field` prop is supplied.
 */
export interface AvatarEntity {
  firstname?: string | null;
  lastname?: string | null;
  avatarUrl?: string | null;
}

// `T` is only constrained to `object`, not to `AvatarEntity`: the whole point of
// the `firstNameField` / `lastNameField` / `avatarUrlField` props is to support
// entities that name those fields differently (see BaseAvatarCell.example.tsx).
// `AvatarEntity` is a weak type (every property optional), so constraining to it
// rejected exactly those entities with TS2559 "no properties in common".
interface BaseAvatarCellProps<T extends object> {
  entity: T;
  onClick?: () => void;
  firstNameField?: keyof T;
  lastNameField?: keyof T;
  avatarUrlField?: keyof T;
  hideDetailsLabel?: boolean;
  nonClickable?: boolean;
  subtitle?: string;
  subtitleField?: keyof T;
}

/**
 * Base avatar cell component that handles avatarUrl field for any entity type
 * Resolves image IDs to URLs using the image service when needed
 * Supports both UUID image IDs and direct URLs (http/https/data/blob/relative paths)
 * 
 * @param entity - The entity containing avatar information
 * @param onClick - Callback when the avatar is clicked (optional for display-only mode)
 * @param firstNameField - Optional custom field name for first name (defaults to 'firstname')
 * @param lastNameField - Optional custom field name for last name (defaults to 'lastname')
 * @param avatarUrlField - Optional custom field name for avatar URL (defaults to 'avatarUrl')
 * @param hideDetailsLabel - Hide the "Details" label (useful for compact displays)
 * @param nonClickable - Render as non-clickable display element
 * @param subtitle - Optional subtitle text to display below the name
 * @param subtitleField - Optional field name to extract subtitle from entity
 */
export function BaseAvatarCell<T extends object>({
  entity,
  onClick,
  firstNameField = 'firstname' as keyof T,
  lastNameField = 'lastname' as keyof T,
  avatarUrlField = 'avatarUrl' as keyof T,
  hideDetailsLabel = false,
  nonClickable = false,
  subtitle,
  subtitleField,
}: BaseAvatarCellProps<T>) {
  // Extract values with proper type safety
  const firstName = String(entity[firstNameField] || '');
  const lastName = String(entity[lastNameField] || '');
  const avatarUrlRaw = entity[avatarUrlField] as string | null | undefined;
  
  // Extract subtitle if field is specified
  const extractedSubtitle = subtitleField ? String(entity[subtitleField] || '') : undefined;
  const finalSubtitle = subtitle || extractedSubtitle;
  
  // Handle empty strings by treating them as null/undefined
  const avatarUrl = avatarUrlRaw?.trim() || undefined;
  
  // Check if avatarUrl is an image ID that needs resolution
  const needsImageResolution = isImageId(avatarUrl);
  
  // Use the useImageUrl hook to resolve image ID to URL if needed
  const resolvedImageUrl = useImageUrl(needsImageResolution ? avatarUrl : null);
  
  // Determine the final avatar URL to use
  // Convert null values to undefined for consistency with UI component expectations
  let finalAvatarUrl: string | undefined;
  if (needsImageResolution) {
    finalAvatarUrl = resolvedImageUrl || undefined;
  } else if (isDirectImageUrl(avatarUrl)) {
    finalAvatarUrl = avatarUrl;
  } else {
    finalAvatarUrl = undefined; // Invalid or empty value
  }
  
  return (
    <AvatarDetailsButton
      firstName={firstName}
      lastName={lastName}
      avatarUrl={finalAvatarUrl}
      onClick={onClick}
      hideDetailsLabel={hideDetailsLabel}
      nonClickable={nonClickable}
      subtitle={finalSubtitle}
    />
  );
}